/**
 * Browser capability detection for the media tools.
 *
 * Tools declare which capabilities they need; the UI probes the browser once
 * and, if anything is missing, shows the "switch browsers" notice instead of a
 * tool that would fail. Encoder/decoder checks must probe an actual codec
 * config (async) — presence of the constructor is not enough.
 */

export type MediaCapability =
  | "webAudio" // decodeAudioData + OfflineAudioContext — universal, sync to check
  | "audioDecode" // WebCodecs AudioDecoder (e.g. AAC from a video container)
  | "audioEncodeOpus" // WebCodecs AudioEncoder, Opus — for compressed audio output
  | "audioEncodeAac" // WebCodecs AudioEncoder, AAC — missing in Firefox & on Linux
  | "videoDecode" // WebCodecs VideoDecoder (H.264)
  | "videoEncode" // WebCodecs VideoEncoder, H.264
  | "videoEncodeVp9" // WebCodecs VideoEncoder, VP9
  | "webgpuF16"; // WebGPU adapter with shader-f16 — required by the fp16 separation model

export type CapabilityReport = Record<MediaCapability, boolean>;

/** Which capabilities each tool requires. Drives the unsupported-browser notice. */
export const TOOL_REQUIREMENTS = {
  "trim-audio": ["webAudio"],
  "convert-audio": ["webAudio"],
  "optimize-audio": ["webAudio"],
  "merge-audio": ["webAudio"],
  "normalize-audio": ["webAudio"],
  // decodeAudioData reads the audio track of mp4/mov/webm directly (verified),
  // so the happy path needs only Web Audio — no WebCodecs demux.
  "extract-audio": ["webAudio"],
  // mediabunny transmuxes (copies packets) for the common case — pure JS, no
  // WebCodecs. Conversion.isValid + a runtime error cover the rare file that
  // would need an unavailable transcode, so no capability is hard-required.
  "trim-video": [],
  "convert-video": [],
  "mute-video": [],
  "compress-video": ["videoDecode", "videoEncode"],
  "rotate-video": ["videoDecode", "videoEncode"],
  // Pure <video>+<canvas> capture; works anywhere.
  "extract-frame": [],
  // The caption editor decodes the source and re-encodes the burned-in result.
  "subtitle-editor": ["videoDecode", "videoEncode"],
  // The stitcher always needs Web Audio and source video decode. Output format
  // support is conditional: MP4 needs H.264/AAC, WebM needs VP9/Opus.
  "video-stitcher": ["videoDecode", "webAudio"],
  "vocal-remover": ["webAudio", "webgpuF16"],
  "speech-to-text": ["webAudio", "webgpuF16"],
} as const satisfies Record<string, readonly MediaCapability[]>;

export type ToolId = keyof typeof TOOL_REQUIREMENTS;

/** Synchronous check for the Web Audio stack (covers every Phase 1 audio tool). */
export function hasWebAudio(): boolean {
  const audioCtx =
    globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: unknown }).webkitAudioContext;
  const offlineCtx =
    globalThis.OfflineAudioContext ??
    (globalThis as { webkitOfflineAudioContext?: unknown }).webkitOfflineAudioContext;
  return typeof audioCtx === "function" && typeof offlineCtx === "function";
}

async function canDecodeVideo(): Promise<boolean> {
  if (typeof globalThis.VideoDecoder === "undefined") return false;
  try {
    const support = await VideoDecoder.isConfigSupported({ codec: "avc1.42001f" });
    return support.supported === true;
  } catch {
    return false;
  }
}

async function canEncodeVideo(): Promise<boolean> {
  if (typeof globalThis.VideoEncoder === "undefined") return false;
  try {
    const support = await VideoEncoder.isConfigSupported({
      codec: "avc1.42001f",
      width: 640,
      height: 360,
    });
    return support.supported === true;
  } catch {
    return false;
  }
}

async function canEncodeVp9Video(): Promise<boolean> {
  if (typeof globalThis.VideoEncoder === "undefined") return false;
  try {
    const support = await VideoEncoder.isConfigSupported({
      codec: "vp09.00.10.08",
      width: 640,
      height: 360,
    });
    return support.supported === true;
  } catch {
    return false;
  }
}

async function canDecodeAudio(): Promise<boolean> {
  if (typeof globalThis.AudioDecoder === "undefined") return false;
  try {
    const support = await AudioDecoder.isConfigSupported({
      codec: "mp4a.40.2",
      sampleRate: 48000,
      numberOfChannels: 2,
    });
    return support.supported === true;
  } catch {
    return false;
  }
}

async function canEncodeAac(): Promise<boolean> {
  if (typeof globalThis.AudioEncoder === "undefined") return false;
  try {
    const support = await AudioEncoder.isConfigSupported({
      codec: "mp4a.40.2",
      sampleRate: 48000,
      numberOfChannels: 2,
    });
    return support.supported === true;
  } catch {
    return false;
  }
}

async function canEncodeOpus(): Promise<boolean> {
  if (typeof globalThis.AudioEncoder === "undefined") return false;
  try {
    const support = await AudioEncoder.isConfigSupported({
      codec: "opus",
      sampleRate: 48000,
      numberOfChannels: 2,
    });
    return support.supported === true;
  } catch {
    return false;
  }
}

async function canUseWebgpuF16(): Promise<boolean> {
  try {
    const navigatorWithGpu = globalThis as typeof globalThis & {
      navigator?: Navigator & {
        gpu?: {
          requestAdapter: () => Promise<{ features: { has: (feature: string) => boolean } } | null>;
        };
      };
    };
    const gpu = navigatorWithGpu.navigator?.gpu;
    if (!gpu) return false;
    const adapter = await gpu.requestAdapter();
    return !!adapter && adapter.features.has("shader-f16");
  } catch {
    return false;
  }
}

/** Probe every capability. Safe to call once and cache for the session. */
export async function detectCapabilities(): Promise<CapabilityReport> {
  const [
    videoDecode,
    videoEncode,
    videoEncodeVp9,
    audioDecode,
    audioEncodeOpus,
    audioEncodeAac,
    webgpuF16,
  ] = await Promise.all([
    canDecodeVideo(),
    canEncodeVideo(),
    canEncodeVp9Video(),
    canDecodeAudio(),
    canEncodeOpus(),
    canEncodeAac(),
    canUseWebgpuF16(),
  ]);
  return {
    webAudio: hasWebAudio(),
    audioDecode,
    audioEncodeOpus,
    audioEncodeAac,
    videoDecode,
    videoEncode,
    videoEncodeVp9,
    webgpuF16,
  };
}

/** Capabilities a tool needs but the browser lacks. Empty array => supported. */
export function missingCapabilities(tool: ToolId, report: CapabilityReport): MediaCapability[] {
  if (tool === "video-stitcher") {
    const missing = TOOL_REQUIREMENTS[tool].filter((capability) => !report[capability]);
    const canExportMp4 = report.videoEncode && report.audioEncodeAac;
    const canExportWebm = report.videoEncodeVp9 && report.audioEncodeOpus;

    if (canExportMp4 || canExportWebm) return missing;

    return [
      ...missing,
      ...(["audioEncodeAac", "audioEncodeOpus", "videoEncode", "videoEncodeVp9"] as const).filter(
        (capability) => !report[capability]
      ),
    ];
  }

  return TOOL_REQUIREMENTS[tool].filter((capability) => !report[capability]);
}
